import { useCallback, useState } from 'react';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import { useSalesforceStatus } from '../hooks/useSalesforceStatus';
import { DisconnectConfirmation } from './DisconnectConfirmation';
import { OneWayBanner } from './OneWayBanner';
import { SalesforceImportProgress } from './SalesforceImportProgress';
import { SalesforceMappingsManagerView } from './SalesforceMappingsManagerView';

interface ConnectionStatusPanelProps {
  orgId: number | string;
}

function formatImportCompletedAt(
  iso: string | null | undefined,
  timeZone: string
): string | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return null;
  }
  // Date-only render, so no `timeZoneName` label — but the zone must still be
  // explicit or the calendar day flips per device (FRONTEND_PATTERNS.md § Timezones).
  return d.toLocaleDateString(undefined, { dateStyle: 'medium', timeZone });
}

export function ConnectionStatusPanel({ orgId }: ConnectionStatusPanelProps) {
  const { data, isLoading } = useSalesforceStatus(orgId);
  const { hasPermissionGroup } = usePermissions();
  const displayTimezone = useDisplayTimezone();
  const [managingMappings, setManagingMappings] = useState(false);
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);

  const handleEditMappings = useCallback(() => {
    setManagingMappings(true);
  }, []);

  const handleBackFromMappings = useCallback(() => {
    setManagingMappings(false);
  }, []);

  const handleDisconnectClick = useCallback(() => {
    setIsDisconnectOpen(true);
  }, []);

  const handleDisconnectClose = useCallback(() => {
    setIsDisconnectOpen(false);
  }, []);

  if (isLoading) {
    return <LoadingIndicator size='sm' />;
  }

  if (!data?.connected) {
    return null;
  }

  const isAdmin = hasPermissionGroup('ahoy_other_admin');

  if (managingMappings) {
    return (
      <div className='card-refined p-6'>
        <SalesforceMappingsManagerView
          orgId={orgId}
          connected
          onBack={handleBackFromMappings}
        />
      </div>
    );
  }

  const importCompletedAt = formatImportCompletedAt(
    data.bulk_completed_at,
    displayTimezone
  );

  return (
    <div className='card-refined p-6'>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0 flex-1'>
          <p className='text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]'>
            Salesforce org
          </p>
          <div className='flex items-center gap-2'>
            <h3 className='truncate text-heading-3 text-[var(--color-text-primary)]'>
              {data.instance_url ?? data.sf_org_id ?? '—'}
            </h3>
            {data.is_sandbox && (
              <Badge type='pill-color' color='warning'>
                Sandbox
              </Badge>
            )}
          </div>
          {importCompletedAt && (
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              Initial import completed {importCompletedAt}
            </p>
          )}
        </div>
        {isAdmin && (
          <div className='flex shrink-0 items-center gap-2'>
            <Button color='secondary' size='sm' onClick={handleEditMappings}>
              Edit field mappings
            </Button>
            <Button color='secondary' size='sm' onClick={handleDisconnectClick}>
              Disconnect
            </Button>
          </div>
        )}
      </div>

      <OneWayBanner className='mt-4' />

      <div className='mt-4'>
        <SalesforceImportProgress importJobs={data.import_jobs} />
      </div>

      <DisconnectConfirmation
        orgId={orgId}
        isOpen={isDisconnectOpen}
        onClose={handleDisconnectClose}
      />
    </div>
  );
}
